import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Check, X, Clock, Users, Calendar, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-orange-100 text-orange-700',
  approved: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
  completed: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  paused: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function ChefBookings() {
  const [inHome, setInHome] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [eventBookings, setEventBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [ih, mp, eb] = await Promise.all([
        API.get('/in-home-bookings'),
        API.get('/meal-plans'),
        API.get('/event-bookings'),
      ]);
      setInHome(ih.data);
      setMealPlans(mp.data);
      setEventBookings(eb.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStatus = async (bookingId, status) => {
    try {
      await API.put(`/in-home-bookings/${bookingId}/status`, { status });
      toast.success(`Booking ${status}`, { description: status === 'approved' ? 'The resident has been notified' : 'The booking has been declined' });
      fetchAll();
    } catch {
      toast.error('Failed to update booking');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#E05A47] border-t-transparent rounded-full animate-spin" /></div>;

  const pending = inHome.filter(b => b.status === 'pending');
  const other = inHome.filter(b => b.status !== 'pending');

  return (
    <div className="fade-in space-y-8" data-testid="chef-bookings-page">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#222] font-[Manrope]">Manage Bookings</h1>

      <Tabs defaultValue="in-home">
        <TabsList className="bg-[#F7F7F7] rounded-lg p-1">
          <TabsTrigger value="in-home" className="rounded-md text-sm font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="chef-tab-in-home">
            In-Home ({inHome.length})
          </TabsTrigger>
          <TabsTrigger value="meal-plans" className="rounded-md text-sm font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="chef-tab-meal-plans">
            Meal Plans ({mealPlans.length})
          </TabsTrigger>
          <TabsTrigger value="events" className="rounded-md text-sm font-semibold data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="chef-tab-events">
            Events ({eventBookings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="in-home" className="mt-4 space-y-6">
          {pending.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-[#E05A47] mb-3">Pending Approval ({pending.length})</h2>
              <div className="space-y-3">
                {pending.map(b => (
                  <div key={b.id} className="bg-white rounded-xl border-2 border-orange-200 p-5" data-testid={`pending-${b.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-bold text-[#222] font-[Manrope]">{b.user_name}</p>
                        <p className="text-sm text-[#717171] capitalize">{b.booking_type?.replace('-', ' ')}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleStatus(b.id, 'approved')} className="bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs gap-1 btn-press" data-testid={`approve-${b.id}`}>
                          <Check className="w-3.5 h-3.5" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleStatus(b.id, 'declined')} className="border-red-300 text-red-600 rounded-lg text-xs gap-1" data-testid={`decline-${b.id}`}>
                          <X className="w-3.5 h-3.5" /> Decline
                        </Button>
                      </div>
                    </div>
                    <div className="mt-3 grid sm:grid-cols-4 gap-3 text-xs text-[#717171]">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{new Date(b.date).toLocaleDateString()}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{b.time}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{b.guests} guests</span>
                      <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />${b.price}</span>
                    </div>
                    {b.menu_preferences?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {b.menu_preferences.map(m => <span key={m} className="px-2 py-0.5 bg-[#F7F7F7] rounded text-xs text-[#222]">{m}</span>)}
                      </div>
                    )}
                    {b.special_requests && <p className="mt-2 text-xs text-[#717171] italic">"{b.special_requests}"</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {other.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-[#717171] mb-3">History</h2>
              <div className="space-y-2">
                {other.map(b => (
                  <div key={b.id} className="bg-white rounded-xl border border-[#EBEBEB] p-4 flex items-center justify-between" data-testid={`history-${b.id}`}>
                    <div>
                      <p className="font-semibold text-sm text-[#222]">{b.user_name} — <span className="capitalize">{b.booking_type?.replace('-', ' ')}</span></p>
                      <p className="text-xs text-[#717171]">{new Date(b.date).toLocaleDateString()} · {b.guests} guests · ${b.price}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[b.status]}`}>{b.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {inHome.length === 0 && <div className="text-center py-12 text-[#717171]">No in-home bookings yet</div>}
        </TabsContent>

        <TabsContent value="meal-plans" className="mt-4 space-y-3">
          {mealPlans.length === 0 ? <div className="text-center py-12 text-[#717171]">No meal plans</div> : mealPlans.map(p => (
            <div key={p.id} className="bg-white rounded-xl border border-[#EBEBEB] p-4 flex items-center justify-between" data-testid={`chef-meal-plan-${p.id}`}>
              <div>
                <p className="font-semibold text-sm text-[#222]">{p.user_name} — <span className="capitalize">{p.plan_type}</span></p>
                <p className="text-xs text-[#717171]">{p.meals_per_day} meals/day · {p.frequency} · {p.duration_weeks} wks · ${p.total_price}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[p.status]}`}>{p.status}</span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="events" className="mt-4 space-y-3">
          {eventBookings.length === 0 ? <div className="text-center py-12 text-[#717171]">No event bookings</div> : eventBookings.map(e => (
            <div key={e.id} className="bg-white rounded-xl border border-[#EBEBEB] p-4 flex items-center justify-between" data-testid={`chef-event-booking-${e.id}`}>
              <div>
                <p className="font-semibold text-sm text-[#222]">{e.user_name} — {e.event_title}</p>
                <p className="text-xs text-[#717171]">{e.seats} seats · ${e.total_price}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusColors[e.status]}`}>{e.status}</span>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
